import { ErrorBoundary } from "@/app/error_boundary"
import { GlobalDataProvider } from "@/app/global_data"
import { mainLogger } from "@/app/logger"
import { AppDataProvider } from "@/app/providers/app_data.provider"
import { UserProvider } from "@/app/providers/user_provider"
import { eventTracker } from "@/app/types"
import { FEATURE_FLAG_KEYS } from "@/app/types/flag_definitions"
import { growthbook } from "@/lib/growthbook"
import { queryClient } from "@/lib/query-client"
import { rollbarConfig } from "@/lib/rollbar_config"
import SEO from "@/next-seo.config"
import { RootLayout } from "@/pages/layout"
import "@/styles/tailwind.css"
import { GrowthBookProvider } from "@growthbook/growthbook-react"
import { Provider as RollbarProvider } from "@rollbar/react"
import { QueryClientProvider } from "@tanstack/react-query"
import { DefaultSeo } from "next-seo"
import type { AppProps } from "next/app"
import { StrictMode, useEffect } from "react"
import { ToastContainer } from "react-toastify"

growthbook.init({ streaming: true })

// wrap all components with the AppData and other providers, apply top level "container" classes
export default function MyApp({ Component, pageProps }: AppProps) {
  useEffect(() => {
    if (
      growthbook.ready &&
      growthbook.isOff(FEATURE_FLAG_KEYS.others.SHOW_LOGS)
    ) {
      mainLogger.disableAll()
    }
  }, [growthbook])

  // monitor app close event to send events
  useEffect(() => {
    const handleAppClose = () => {
      eventTracker
        .trackEvent({ event: "app:close" })
        .then(() => eventTracker.sendEvents())
    }

    window.addEventListener("beforeunload", handleAppClose)

    return () => {
      window.removeEventListener("beforeunload", handleAppClose)
    }
  }, [])

  return (
    <StrictMode>
      <ToastContainer limit={1} />
      <DefaultSeo {...SEO} />
      <RollbarProvider config={rollbarConfig}>
        <GrowthBookProvider growthbook={growthbook}>
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
              <UserProvider>
                <AppDataProvider>
                  <GlobalDataProvider>
                    {Component["useLayout"] ?
                      Component["useLayout"](<Component {...pageProps} />)
                    : <RootLayout>
                        <Component {...pageProps} />
                      </RootLayout>
                    }
                  </GlobalDataProvider>
                </AppDataProvider>
              </UserProvider>
            </ErrorBoundary>
          </QueryClientProvider>
        </GrowthBookProvider>
      </RollbarProvider>
    </StrictMode>
  )
}
